<script setup lang="ts">
import type { IChipSize, IChipVariant } from "./chip.interface"
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import { Icon } from "@/components/ui"

interface ChipProps {
  prefixIcon?: IconKeys
  variant?: IChipVariant
  size?: IChipSize
  label?: string
  clearable?: boolean
  disabled?: boolean
  isLoading?: boolean
}

withDefaults(defineProps<ChipProps>(), {
  variant: "default",
  size: "base",
  label: "",
  clearable: false,
  disabled: false,
  isLoading: false,
})

const emit = defineEmits(["delete"])

function onDelete() {
  emit("delete")
}
</script>

<template>
  <div
    class="py-1 px-3 rounded-full" :class="[
      {
        'bg-[#FCF3F3] text-[#E58783] border border-[#E58783]': variant === 'default',
        'bg-nav-active text-white font-semibold': variant === 'pink-solid',
        'bg-[#e7e7e7] text-black border-none hover:bg-[#cecece]': variant === 'tag',
        'bg-success border-none uppercase text-[#072a47]': variant === 'success',
        'bg-danger border-none uppercase text-[#072a47]': variant === 'danger',
        'bg-warning border-none uppercase text-[#072a47]': variant === 'warning',
        'bg-secondary border-none uppercase text-secondary-card-text': variant === 'secondary',
        'bg-[#E7E7E7] border-none uppercase text-[#072a47]': variant === 'info',
        'bg-[#cecece]': disabled,
        'py-1 px-3 text-sm': size === 'sm',
        'py-2 px-4 text-base': size === 'base',
        'py-3 px-5 text-md': size === 'md',
        'py-4 px-6 text-lg': size === 'lg',
      },
    ]"
  >
    <div class="flex grow-0 gap-1 items-center justify-center whitespace-nowrap">
      <slot name="prefixIcon">
        <Icon v-if="prefixIcon" :icon="prefixIcon" size="sm" />
      </slot>
      <slot>
        <span class="font-comfortaa font-bold text-sm">{{ label }}</span>
      </slot>
      <button
        v-if="variant === 'tag' && !disabled && clearable" :disabled="isLoading"
        class="flex items-center bg-transparent border-none p-0 m-0 disabled:cursor-not-allowed"
        @click="onDelete"
      >
        <Icon
          class="gpx-chip-close-icon" :class="{ 'gpx-chip-close-icon_active': !isLoading }" icon="Close"
        />
      </button>
    </div>
  </div>
</template>

<style scoped>
.gpx-chip-close-icon {
  width: 10px;
}

.gpx-chip-close-icon_active:hover {
  transform: scale(1.5);
}
</style>
